import React, { useState, useEffect, useRef } from 'react';
import { Send, Bot, X, Phone, Mail, MessageCircle, User } from 'lucide-react';

const AcademyAIChatbot = () => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [userInfo, setUserInfo] = useState({});
  const [showChat, setShowChat] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [chatStage, setChatStage] = useState('initial');
  const messagesEndRef = useRef(null);

  const ACADEMY_PHONE = '8639449423';
  const ACADEMY_EMAIL = 'olewinacademy@gmail.com';
  const ACADEMY_NAME = 'Olewin Academy';

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (showChat && messages.length === 0) {
      addBotMessage(`🎓 Hello! Welcome to ${ACADEMY_NAME}! 👋

I'm your personal AI assistant, here to help you explore our world-class training programs.

📚 WE OFFER:

💻 SOFTWARE & TECHNOLOGY TRAINING:
• Quantum Computing
• Space Technology
• Data Science & Machine Learning
• Big Data Engineering
• Cloud Computing (AWS, Azure, GCP)
• Artificial Intelligence & Deep Learning
• Full Stack Development
• Blockchain & Cryptocurrency
• Cybersecurity & Ethical Hacking
• IoT & Embedded Systems

🎯 COMPETITIVE EXAM PREPARATION:
• IIT-JEE (Foundation & Advanced)
• NEET Medical (UG & PG)
• GATE (All Branches)
• IIT-JAM (MSc Entrance)
• CSIR-UGC NET
• UPSC Civil Services
• SSC (CGL, CHSL, CPO)
• CA (Foundation & Intermediate)

✨ BENEFITS:
✅ Expert IIT Alumni Mentorship
✅ Paid Internship Opportunities
✅ Hands-on Real-world Projects
✅ Placement Assistance
✅ Flexible EMI Payment Options

Before we begin, may I know your good name?`, 500);
      setChatStage('waiting_name');
    }
  }, [showChat]);

  const addBotMessage = (text, delay = 1500) => {
    setIsTyping(true);
    setTimeout(() => {
      setMessages(prev => [...prev, {
        text: text,
        sender: 'bot',
        timestamp: new Date()
      }]);
      setIsTyping(false);
    }, delay);
  };

  const addUserMessage = (text) => {
    setMessages(prev => [...prev, {
      text: text,
      sender: 'user',
      timestamp: new Date()
    }]);
  };

  const courseFees = {
    software: {
      'quantum computing': { total: 130000, registration: 10000, emi1: 37500, emi2: 37500, duration: '6 months' },
      'quantum': { total: 130000, registration: 10000, emi1: 37500, emi2: 37500, duration: '6 months' },
      'space technology': { total: 200000, registration: 10000, emi1: 37500, emi2: 37500, duration: '6 months' },
      'space': { total: 200000, registration: 10000, emi1: 37500, emi2: 37500, duration: '6 months' },
      'satellite': { total: 200000, registration: 10000, emi1: 37500, emi2: 37500, duration: '6 months' },
      'data science': { total: 95000, registration: 12000, emi1: 41500, emi2: 41500, duration: '8 months' },
      'machine learning': { total: 95000, registration: 12000, emi1: 41500, emi2: 41500, duration: '8 months' },
      'ml': { total: 95000, registration: 12000, emi1: 41500, emi2: 41500, duration: '8 months' },
      'big data': { total: 90000, registration: 11000, emi1: 39500, emi2: 39500, duration: '6 months' },
      'hadoop': { total: 90000, registration: 11000, emi1: 39500, emi2: 39500, duration: '6 months' },
      'cloud computing': { total: 75000, registration: 9000, emi1: 33000, emi2: 33000, duration: '5 months' },
      'cloud': { total: 75000, registration: 9000, emi1: 33000, emi2: 33000, duration: '5 months' },
      'aws': { total: 75000, registration: 9000, emi1: 33000, emi2: 33000, duration: '5 months' },
      'azure': { total: 75000, registration: 9000, emi1: 33000, emi2: 33000, duration: '5 months' },
      'gcp': { total: 75000, registration: 9000, emi1: 33000, emi2: 33000, duration: '5 months' },
      'ai': { total: 98000, registration: 12000, emi1: 43000, emi2: 43000, duration: '8 months' },
      'artificial intelligence': { total: 98000, registration: 12000, emi1: 43000, emi2: 43000, duration: '8 months' },
      'deep learning': { total: 98000, registration: 12000, emi1: 43000, emi2: 43000, duration: '8 months' },
      'full stack': { total: 80000, registration: 10000, emi1: 35000, emi2: 35000, duration: '7 months' },
      'fullstack': { total: 80000, registration: 10000, emi1: 35000, emi2: 35000, duration: '7 months' },
      'python': { total: 65000, registration: 8000, emi1: 28500, emi2: 28500, duration: '5 months' },
      'java': { total: 65000, registration: 8000, emi1: 28500, emi2: 28500, duration: '5 months' },
      'blockchain': { total: 75000, registration: 9000, emi1: 33000, emi2: 33000, duration: '5 months' },
      'cryptocurrency': { total: 75000, registration: 9000, emi1: 33000, emi2: 33000, duration: '5 months' },
      'crypto': { total: 75000, registration: 9000, emi1: 33000, emi2: 33000, duration: '5 months' },
      'cybersecurity': { total: 85000, registration: 10000, emi1: 37500, emi2: 37500, duration: '6 months' },
      'cyber security': { total: 85000, registration: 10000, emi1: 37500, emi2: 37500, duration: '6 months' },
      'ethical hacking': { total: 85000, registration: 10000, emi1: 37500, emi2: 37500, duration: '6 months' },
      'hacking': { total: 85000, registration: 10000, emi1: 37500, emi2: 37500, duration: '6 months' },
      'iot': { total: 70000, registration: 8500, emi1: 30750, emi2: 30750, duration: '5 months' },
      'embedded systems': { total: 70000, registration: 8500, emi1: 30750, emi2: 30750, duration: '5 months' },
      'embedded': { total: 70000, registration: 8500, emi1: 30750, emi2: 30750, duration: '5 months' }
    },
    competitive: {
      'iit-jee foundation': { total: 120000, registration: 15000, emi1: 52500, emi2: 52500, duration: '1-2 years' },
      'jee foundation': { total: 120000, registration: 15000, emi1: 52500, emi2: 52500, duration: '1-2 years' },
      'foundation': { total: 120000, registration: 15000, emi1: 52500, emi2: 52500, duration: '1-2 years' },
      'iit-jee': { total: 150000, registration: 20000, emi1: 65000, emi2: 65000, duration: '2 years' },
      'iit jee': { total: 150000, registration: 20000, emi1: 65000, emi2: 65000, duration: '2 years' },
      'jee': { total: 150000, registration: 20000, emi1: 65000, emi2: 65000, duration: '2 years' },
      'jee mains': { total: 150000, registration: 20000, emi1: 65000, emi2: 65000, duration: '2 years' },
      'jee advanced': { total: 150000, registration: 20000, emi1: 65000, emi2: 65000, duration: '2 years' },
      'neet': { total: 140000, registration: 18000, emi1: 61000, emi2: 61000, duration: '2 years' },
      'neet ug': { total: 140000, registration: 18000, emi1: 61000, emi2: 61000, duration: '2 years' },
      'neet pg': { total: 160000, registration: 22000, emi1: 69000, emi2: 69000, duration: '1 year' },
      'medical': { total: 140000, registration: 18000, emi1: 61000, emi2: 61000, duration: '2 years' },
      'gate': { total: 95000, registration: 12000, emi1: 41500, emi2: 41500, duration: '8-12 months' },
      'iit-jam': { total: 85000, registration: 11000, emi1: 37000, emi2: 37000, duration: '8-12 months' },
      'iit jam': { total: 85000, registration: 11000, emi1: 37000, emi2: 37000, duration: '8-12 months' },
      'jam': { total: 85000, registration: 11000, emi1: 37000, emi2: 37000, duration: '8-12 months' },
      'csir': { total: 75000, registration: 10000, emi1: 32500, emi2: 32500, duration: '8-10 months' },
      'ugc': { total: 75000, registration: 10000, emi1: 32500, emi2: 32500, duration: '8-10 months' },
      'net': { total: 75000, registration: 10000, emi1: 32500, emi2: 32500, duration: '8-10 months' },
      'csir-ugc net': { total: 75000, registration: 10000, emi1: 32500, emi2: 32500, duration: '8-10 months' },
      'upsc': { total: 180000, registration: 25000, emi1: 77500, emi2: 77500, duration: '12-18 months' },
      'ias': { total: 180000, registration: 25000, emi1: 77500, emi2: 77500, duration: '12-18 months' },
      'ips': { total: 180000, registration: 25000, emi1: 77500, emi2: 77500, duration: '12-18 months' },
      'ifs': { total: 180000, registration: 25000, emi1: 77500, emi2: 77500, duration: '12-18 months' },
      'civil services': { total: 180000, registration: 25000, emi1: 77500, emi2: 77500, duration: '12-18 months' },
      'ssc': { total: 65000, registration: 8000, emi1: 28500, emi2: 28500, duration: '6-8 months' },
      'ssc cgl': { total: 65000, registration: 8000, emi1: 28500, emi2: 28500, duration: '6-8 months' },
      'ssc chsl': { total: 60000, registration: 7500, emi1: 26250, emi2: 26250, duration: '6-8 months' },
      'ca': { total: 110000, registration: 14000, emi1: 48000, emi2: 48000, duration: '10-12 months' },
      'ca foundation': { total: 90000, registration: 11000, emi1: 39500, emi2: 39500, duration: '8-10 months' },
      'ca intermediate': { total: 120000, registration: 15000, emi1: 52500, emi2: 52500, duration: '12 months' },
      'chartered accountant': { total: 110000, registration: 14000, emi1: 48000, emi2: 48000, duration: '10-12 months' }
    }
  };

  const getCourseFee = (query) => {
    const lowerQuery = query.toLowerCase().trim();
    
    const allCourses = { ...courseFees.software, ...courseFees.competitive };
    const sortedKeywords = Object.keys(allCourses).sort((a, b) => b.length - a.length);
    
    for (const keyword of sortedKeywords) {
      if (lowerQuery.includes(keyword)) {
        const fee = allCourses[keyword];
        const isCompetitive = courseFees.competitive.hasOwnProperty(keyword);
        
        return {
          found: true,
          category: isCompetitive ? 'Competitive Exam' : 'Software & Technology',
          course: keyword.toUpperCase().replace(/-/g, ' '),
          ...fee
        };
      }
    }
    
    return { found: false };
  };

  const generateCourseResponse = (query) => {
    const lowerQuery = query.toLowerCase().trim();

    // Check for fee-related queries
    if (lowerQuery.includes('fee') || lowerQuery.includes('cost') || lowerQuery.includes('price') || lowerQuery.includes('charge')) {
      const feeInfo = getCourseFee(lowerQuery);
      
      if (feeInfo.found) {
        return `💰 ${feeInfo.course} - FEE DETAILS

📚 Category: ${feeInfo.category}
⏱️ Duration: ${feeInfo.duration}

💵 TOTAL FEE: ₹${feeInfo.total.toLocaleString('en-IN')}

📋 PAYMENT PLAN (3 Easy Installments):

1️⃣ Registration Fee: ₹${feeInfo.registration.toLocaleString('en-IN')}
   • Secure your seat
   • Course materials & resources
   • Lifetime access to recordings

2️⃣ First EMI: ₹${feeInfo.emi1.toLocaleString('en-IN')}
   • Personal mentorship begins
   • Hands-on training starts

3️⃣ Second EMI: ₹${feeInfo.emi2.toLocaleString('en-IN')}
   • Complete course access
   • Project guidance
   • Placement preparation

✅ INCLUDES:
✔️ Expert IIT Alumni Mentorship
✔️ Paid Internship Opportunities
✔️ Real-world Projects
✔️ Placement Assistance
✔️ Course Completion Certificate

📞 Ready to enroll?
Call: +91 ${ACADEMY_PHONE}
Email: ${ACADEMY_EMAIL}

Ask me about another course or type "courses" to see all options!`;
      }
    }

    // Data Science
    if (lowerQuery.includes('data science')) {
      return `📊 DATA SCIENCE & MACHINE LEARNING

⏱️ Duration: 8 months
💰 Total Fee: ₹95,000

📚 CURRICULUM:
• Python Programming (NumPy, Pandas)
• Statistical Analysis & Probability
• Machine Learning Algorithms
• Deep Learning (TensorFlow, PyTorch)
• Natural Language Processing
• Computer Vision
• Big Data Tools (Spark, Hadoop)
• Model Deployment & MLOps

🎯 PROJECTS:
• Predictive Analytics Dashboard
• Recommendation System
• Image Classification
• Sentiment Analysis
• Time Series Forecasting

💼 CAREER PATH:
• Data Scientist roles
• ML Engineer positions
• High-paying opportunities
• Paid internships included

📞 Want fee details? Ask "data science fee"
Or call: +91 ${ACADEMY_PHONE}`;
    }

    // Machine Learning
    if (lowerQuery.includes('machine learning') || lowerQuery === 'ml') {
      return `🤖 MACHINE LEARNING & AI

⏱️ Duration: 8 months
💰 Total Fee: ₹95,000

📚 WHAT YOU'LL LEARN:
• Python & ML Libraries
• Supervised Learning
• Unsupervised Learning
• Deep Learning & Neural Networks
• TensorFlow & PyTorch
• Model Optimization
• Production Deployment

🎯 PROJECTS:
• Classification Models
• Regression Analysis
• Clustering Applications
• Image Recognition
• NLP Applications

💼 BENEFITS:
• Industry-ready skills
• Portfolio projects
• Placement support
• Paid internships

📞 For fee details: Ask "ML fee"
Contact: +91 ${ACADEMY_PHONE}`;
    }

    // Blockchain
    if (lowerQuery.includes('blockchain') || lowerQuery.includes('crypto')) {
      return `⛓️ BLOCKCHAIN & CRYPTOCURRENCY

⏱️ Duration: 5 months
💰 Total Fee: ₹75,000

📚 LEARN:
• Blockchain Fundamentals
• Solidity Programming
• Smart Contracts
• Ethereum Development
• Web3.js & DApp Creation
• DeFi Applications
• NFT Development
• Cryptocurrency Creation

🎯 BUILD:
• Your Own Cryptocurrency
• Smart Contracts
• DeFi Platform
• NFT Marketplace
• Decentralized Apps

💼 CAREER:
• Blockchain Developer
• Web3 Engineer
• Smart Contract Developer
• High-demand field

📞 Ask "blockchain fee" for payment details
Contact: +91 ${ACADEMY_PHONE}`;
    }

    // AI
    if ((lowerQuery.includes('ai ') || lowerQuery.includes('artificial intelligence') || lowerQuery === 'ai') 
        && !lowerQuery.includes('blockchain')) {
      return `🧠 ARTIFICIAL INTELLIGENCE & DEEP LEARNING

⏱️ Duration: 8 months
💰 Total Fee: ₹98,000

📚 CURRICULUM:
• Python & AI Libraries
• Neural Networks
• Deep Learning (TensorFlow, PyTorch)
• Computer Vision (OpenCV, YOLO)
• Natural Language Processing
• Reinforcement Learning
• GANs & Transformers
• Production AI & MLOps

🎯 PROJECTS:
• AI Chatbot
• Face Recognition
• Object Detection
• Text Generation
• Recommendation Engine
• Autonomous Systems

💼 OPPORTUNITIES:
• AI Engineer roles
• Research positions
• High-salary careers
• Cutting-edge tech

📞 Ask "AI fee" for details
Contact: +91 ${ACADEMY_PHONE}`;
    }

    // Cloud Computing
    if (lowerQuery.includes('cloud') || lowerQuery.includes('aws') || lowerQuery.includes('azure') || lowerQuery.includes('gcp')) {
      return `☁️ MULTI-CLOUD COMPUTING

⏱️ Duration: 5 months
💰 Total Fee: ₹75,000

📚 PLATFORMS:
• AWS (EC2, S3, Lambda, RDS)
• Microsoft Azure
• Google Cloud Platform
• Docker & Kubernetes
• Cloud Architecture
• DevOps & CI/CD
• Cloud Security

🎯 LEARN:
• Deploy Scalable Apps
• Serverless Architecture
• Container Orchestration
• Multi-cloud Solutions
• Infrastructure as Code

💼 CERTIFICATIONS:
• AWS Certified
• Azure Certified
• GCP Certified
• High-demand skills

📞 Ask "cloud fee" for payment plan
Contact: +91 ${ACADEMY_PHONE}`;
    }

    // IIT-JEE
    if (lowerQuery.includes('iit') || lowerQuery.includes('jee')) {
      return `🎓 IIT-JEE PREPARATION

📚 PROGRAMS:

1️⃣ IIT-JEE Foundation (8th, 9th, 10th)
   ⏱️ Duration: 1-2 years
   💰 Fee: ₹1,20,000
   • Strong fundamentals
   • Olympiad prep
   • Early start advantage

2️⃣ IIT-JEE Mains & Advanced (11th, 12th)
   ⏱️ Duration: 2 years
   💰 Fee: ₹1,50,000
   • Complete JEE coverage
   • Daily tests
   • Mock exams

📖 SUBJECTS:
Physics, Chemistry, Mathematics

👨‍🏫 FACULTY:
• IIT Alumni mentors
• Proven track record
• Personalized attention

📞 Ask "IIT-JEE fee" for payment plan
Contact: +91 ${ACADEMY_PHONE}`;
    }

    // NEET
    if (lowerQuery.includes('neet') || lowerQuery.includes('medical')) {
      return `🏥 NEET MEDICAL PREPARATION

📚 PROGRAMS:

1️⃣ NEET UG
   ⏱️ Duration: 2 years
   💰 Fee: ₹1,40,000

2️⃣ NEET PG
   ⏱️ Duration: 1 year
   💰 Fee: ₹1,60,000

📖 SUBJECTS:
• Physics (Medical focus)
• Chemistry (Organic & Inorganic)
• Biology (Botany & Zoology)
• NCERT mastery
• MCQ practice

🎯 FEATURES:
• Daily practice
• Weekly tests
• Mock exams
• NCERT focus

📞 Ask "NEET fee" for details
Contact: +91 ${ACADEMY_PHONE}`;
    }

    // GATE
    if (lowerQuery.includes('gate')) {
      return `🎯 GATE PREPARATION

⏱️ Duration: 8-12 months
💰 Total Fee: ₹95,000

📚 COVERAGE:
• Core Engineering Subjects
• Engineering Mathematics
• General Aptitude
• Previous Year Analysis
• Problem-Solving Techniques

🎯 PRACTICE:
• Subject-wise tests
• Weekly mock exams
• Full-length GATE tests
• Formula sheets

👨‍🏫 FACULTY:
• Subject experts
• PSU prep included
• Career guidance

📞 Ask "GATE fee" for payment plan
Contact: +91 ${ACADEMY_PHONE}`;
    }

    // IIT-JAM
    if (lowerQuery.includes('jam')) {
      return `🎓 IIT-JAM (MSc ENTRANCE)

⏱️ Duration: 8-12 months
💰 Total Fee: ₹85,000

📚 SUBJECTS:
• Physics
• Chemistry
• Mathematics
• Biotechnology
• Mathematical Statistics

📖 COVERAGE:
• Complete syllabus
• Concept building
• Previous year papers
• Mock exams

💼 BENEFITS:
• MSc in IITs
• Research opportunities
• PhD pathway

📞 Ask "JAM fee" for details
Contact: +91 ${ACADEMY_PHONE}`;
    }

    // UPSC
    if (lowerQuery.includes('upsc') || lowerQuery.includes('ias') || lowerQuery.includes('ips') || lowerQuery.includes('civil')) {
      return `🏛️ UPSC CIVIL SERVICES

⏱️ Duration: 12-18 months
💰 Total Fee: ₹1,80,000

📚 COVERAGE:
• General Studies (Complete)
• Current Affairs
• Optional Subject
• Essay Writing
• Answer Writing
• Interview Preparation
• CSAT

🎯 PRACTICE:
• Daily answer writing
• Weekly tests
• Mock interviews
• Current affairs updates

📞 Ask "UPSC fee" for payment plan
Contact: +91 ${ACADEMY_PHONE}`;
    }

    // List all courses
    if (lowerQuery.includes('course') || lowerQuery.includes('all') || lowerQuery.includes('list') || lowerQuery.includes('show')) {
      return `📚 OUR COMPLETE COURSE LIST

💻 SOFTWARE & TECHNOLOGY:
1. Quantum Computing - ₹1,50,000
2. Space Technology - ₹2,00,000
3. Data Science & ML - ₹95,000
4. Big Data Engineering - ₹90,000
5. Cloud Computing - ₹75,000
6. AI & Deep Learning - ₹98,000
7. Full Stack Development - ₹80,000
8. Blockchain - ₹75,000
9. Cybersecurity - ₹85,000
10. IoT & Embedded - ₹70,000

🎯 COMPETITIVE EXAMS:
1. IIT-JEE Foundation - ₹1,20,000
2. IIT-JEE (11th/12th) - ₹1,50,000
3. NEET UG - ₹1,40,000
4. NEET PG - ₹1,60,000
5. GATE - ₹95,000
6. IIT-JAM - ₹85,000
7. CSIR-UGC NET - ₹75,000
8. UPSC - ₹1,80,000
9. SSC - ₹60,000-65,000
10. CA - ₹90,000-1,20,000

Type any course name for details!
Example: "Data Science" or "IIT-JEE"

Or ask for fees: "Data Science fee"`;
    }

    // Help/Default response
    return `I'm here to help! 😊

You can ask me about:

💻 SOFTWARE COURSES:
• Data Science
• Machine Learning
• AI & Deep Learning
• Blockchain
• Cloud Computing
• Full Stack
• Cybersecurity

🎯 COMPETITIVE EXAMS:
• IIT-JEE
• NEET
• GATE
• UPSC
• IIT-JAM

📋 COMMANDS:
• Type "courses" - See all courses
• Type course name - Get details
• Type "course fee" - Get pricing

Example: "Data Science" or "IIT-JEE fee"

What interests you?`;
  };

  const handleSendMessage = () => {
    if (!input.trim()) return;

    const userInput = input.trim();
    addUserMessage(userInput);
    setInput('');

    setTimeout(() => {
      let response = '';

      switch (chatStage) {
        case 'waiting_name':
          setUserInfo(prev => ({ ...prev, name: userInput }));
          response = `Nice to meet you, ${userInput}! 😊

To provide personalized course information, I need your contact details.

What is your mobile number?
(Please enter 10-digit number)`;
          setChatStage('waiting_mobile');
          break;

        case 'waiting_mobile':
          const mobile = userInput.replace(/\D/g, '');
          if (mobile.length === 10) {
            setUserInfo(prev => ({ ...prev, mobile: mobile }));
            response = `Great! Thank you for sharing your mobile number.

Now, what is your email address?
(We'll send course materials here)`;
            setChatStage('waiting_email');
          } else {
            response = `Please enter a valid 10-digit mobile number.

Example: 9876543210`;
          }
          break;

        case 'waiting_email':
          if (userInput.includes('@') && userInput.includes('.')) {
            setUserInfo(prev => ({ ...prev, email: userInput }));
            response = `Perfect! Your details are saved:

✅ Name: ${userInfo.name}
✅ Mobile: +91 ${userInfo.mobile}
✅ Email: ${userInput}

Now, how can I help you today?

You can:
• Type "courses" - See all courses
• Ask about specific course - "Data Science"
• Ask about fees - "Data Science fee"
• Ask about exams - "IIT-JEE"

📞 Or call us directly: +91 ${ACADEMY_PHONE}

What interests you?`;
            setChatStage('conversation');
          } else {
            response = `Please enter a valid email address.

Example: yourname@gmail.com`;
          }
          break;

        case 'conversation':
          response = generateCourseResponse(userInput);
          break;

        default:
          response = `I'm here to help! What would you like to know?`;
      }

      addBotMessage(response, 2000);
    }, 1000);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <>
      <style>{`
        .chat-container {
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
        }
        .chat-header {
          background: linear-gradient(135deg, #059669 0%, #047857 100%);
        }
        .bot-message {
          background: #f0fdf4;
          border-left: 4px solid #059669;
          color: #1f2937;
        }
        .user-message {
          background: linear-gradient(135deg, #059669 0%, #047857 100%);
          color: white;
        }
        .typing-indicator {
          display: flex;
          gap: 4px;
          padding: 10px;
        }
        .typing-indicator span {
          width: 8px;
          height: 8px;
          background: #059669;
          border-radius: 50%;
          animation: typing 1.4s infinite;
        }
        .typing-indicator span:nth-child(2) {
          animation-delay: 0.2s;
        }
        .typing-indicator span:nth-child(3) {
          animation-delay: 0.4s;
        }
        @keyframes typing {
          0%, 60%, 100% { transform: translateY(0); }
          30% { transform: translateY(-10px); }
        }
        .chat-button {
          background: linear-gradient(135deg, #000000ff 0%, #000000ff 100%);
          border: none;
          box-shadow: 0 4px 20px rgba(5, 150, 105, 0.4);
          transition: transform 0.3s;
        }
        .chat-button:hover {
          transform: scale(1.1);
          box-shadow: 0 6px 25px rgba(5, 150, 105, 0.6);
        }
        .send-button {
          background: linear-gradient(135deg, #059669 0%, #047857 100%);
          border: none;
        }
        .send-button:hover {
          background: linear-gradient(135deg, #047857 0%, #065f46 100%);
        }
        .send-button:disabled {
          background: #d1d5db;
          opacity: 0.5;
        }
        .academy-logo {
          width: 40px;
          height: 40px;
          background: white;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-right: 10px;
          flex-shrink: 0;
        }
        @media (max-width: 768px) {
          .chat-window {
            width: 100vw !important;
            height: 100vh !important;
            border-radius: 0 !important;
            position: fixed !important;
            top: 0 !important;
            left: 0 !important;
            right: 0 !important;
            bottom: 0 !important;
          }
        }
      `}</style>

      <div className="position-fixed bottom-0 end-0 p-3 chat-container" style={{ zIndex: 1050 }}>
        {!showChat && (
          <button
            onClick={() => setShowChat(true)}
            className="btn chat-button text-white rounded-circle d-flex align-items-center justify-content-center"
            style={{ width: '75px', height: '75px' }}
            title="Chat with us"
          >
            <MessageCircle size={32} />
          </button>
        )}

        {showChat && (
          <div 
            className="card shadow-lg chat-window" 
            style={{ 
              width: '440px', 
              height: '600px',
              maxWidth: '95vw',
              maxHeight: '95vh',
              borderRadius: '15px',
              overflow: 'hidden'
            }}
          >
            <div className="chat-header text-white p-3 d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center flex-grow-1">
                <div className="academy-logo">
                  <Bot size={24} className="text-success" />
                </div>
                <div className="flex-grow-1">
                  <h6 className="mb-0 fw-bold">{ACADEMY_NAME}</h6>
                  <small style={{ fontSize: '0.75rem', opacity: 0.9 }}>
                    AI Assistant • Online Now
                  </small>
                </div>
              </div>
              <button 
                onClick={() => setShowChat(false)}
                className="btn btn-sm btn-link text-white p-0"
                style={{ textDecoration: 'none' }}
                title="Close chat"
              >
                <X size={24} />
              </button>
            </div>

            <div className="bg-white border-bottom p-2 text-center">
              <small className="text-muted d-flex align-items-center justify-content-center gap-2 flex-wrap" style={{ fontSize: '0.7rem' }}>
                <span className="d-flex align-items-center gap-1">
                  <Phone size={12} /> +91 {ACADEMY_PHONE}
                </span>
                <span className="mx-1">|</span>
                <span className="d-flex align-items-center gap-1">
                  <Mail size={12} /> {ACADEMY_EMAIL}
                </span>
              </small>
            </div>

            <div 
              className="overflow-auto p-3" 
              style={{ 
                height: 'calc(100% - 160px)',
                backgroundColor: '#f9fafb'
              }}
            >
              {messages.map((msg, idx) => (
                <div
                  key={idx}
                  className={`d-flex mb-3 ${msg.sender === 'user' ? 'justify-content-end' : 'justify-content-start'}`}
                >
                  {msg.sender === 'bot' && (
                    <div className="me-2 flex-shrink-0">
                      <div style={{ 
                        width: '36px', 
                        height: '36px', 
                        background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <Bot size={20} className="text-white" />
                      </div>
                    </div>
                  )}
                  <div
                    className={`p-3 rounded ${
                      msg.sender === 'user'
                        ? 'user-message'
                        : 'bot-message'
                    }`}
                    style={{ 
                      maxWidth: '75%',
                      whiteSpace: 'pre-line',
                      wordBreak: 'break-word',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      lineHeight: '1.6'
                    }}
                  >
                    <div style={{ fontSize: '0.9rem' }}>
                      {msg.text}
                    </div>
                    <div 
                      style={{ 
                        fontSize: '0.65rem', 
                        opacity: 0.7,
                        marginTop: '8px'
                      }}
                    >
                      {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </div>
                  {msg.sender === 'user' && (
                    <div className="ms-2 flex-shrink-0">
                      <div style={{ 
                        width: '36px', 
                        height: '36px', 
                        background: '#e5e7eb',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <User size={20} style={{ color: '#6b7280' }} />
                      </div>
                    </div>
                  )}
                </div>
              ))}
              {isTyping && (
                <div className="d-flex align-items-center mb-3">
                  <div className="me-2 flex-shrink-0">
                    <div style={{ 
                      width: '36px', 
                      height: '36px', 
                      background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Bot size={20} className="text-white" />
                    </div>
                  </div>
                  <div className="bot-message p-3 rounded" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                    <div className="typing-indicator">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            <div className="bg-white border-top p-3">
              <div className="input-group">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Type your message..."
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  style={{ 
                    borderRadius: '25px 0 0 25px',
                    border: '2px solid #e5e7eb',
                    padding: '12px 20px',
                    fontSize: '0.9rem'
                  }}
                />
                <button 
                  className="btn send-button text-white" 
                  onClick={handleSendMessage}
                  disabled={!input.trim()}
                  style={{ 
                    borderRadius: '0 25px 25px 0',
                    padding: '0 20px'
                  }}
                  title="Send message"
                >
                  <Send size={20} />
                </button>
              </div>
              <div className="text-center mt-2">
                <small className="text-muted" style={{ fontSize: '0.7rem' }}>
                  Powered by {ACADEMY_NAME} AI • Available 24/7
                </small>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default AcademyAIChatbot;